import { useCallback, useState } from 'react';

import { Button, CloseButton, Drawer, Field, Flex, Portal, useSlotRecipe } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { SearchEngineMobileDrawerProps } from './search-engine-mobile-drawer.props';

import { defaultSearchEngineTranslation, GuestsPickerInputDataModel } from '../../../models';
import {
    GuestsPicker,
    RangePicker,
    RangePickerDates,
    SearchEngineInstantReservationCheckbox,
    SearchLocation,
} from '../../molecules';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';

export const SearchEngineMobileDrawer = <T extends AutocompleteBaseItemModel>({
    setValue,
    getValues,
    register,
    onSubmit,
    isSubmitted,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    isOpen,
    onClose,
    watch,
    isV7SearchEngineVersion,
    autocompleteData,
    isWebComponentVersion = false,
    isMapSearch = false,
}: SearchEngineMobileDrawerProps<T>) => {
    const recipe = useSlotRecipe({
        key: 'searchEngineMobileDrawer',
    });
    const styles = recipe();

    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            onSubmit();
            setTimeout(() => {
                onClose();
            }, 0);
        }, 0);
    }, [onSubmit]);

    return (
        <Drawer.Root
            placement={'top'}
            open={isOpen}
            modal={false}
            onOpenChange={details => {
                setTimeout(() => (!details.open ? onClose() : undefined), 0);
            }}
            onExitComplete={() => setIsLoading(false)}
        >
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content height="auto" className="search-engine-container">
                        <Drawer.CloseTrigger top={0.5} right={0.5} asChild>
                            <CloseButton>
                                <IconsLinear.Close />
                            </CloseButton>
                        </Drawer.CloseTrigger>
                        <Drawer.Header css={styles.drawerHeader}>
                            {translationData.drawerHeader}
                        </Drawer.Header>
                        <Drawer.Body py={4} px={6}>
                            <form onSubmit={onSubmit}>
                                <Flex direction={'column'} gap={4}>
                                    {isV7SearchEngineVersion ? (
                                        <SearchLocation
                                            autocompleteData={autocompleteData}
                                            inputProps={{
                                                inputProps: {
                                                    ...register('location'),
                                                    'data-testid': 'search-engine-location-input',
                                                },
                                                label: translationData.searchLocationLabel,
                                                labelProps: {
                                                    css: styles.label,
                                                },
                                            }}
                                            w={'100%'}
                                        />
                                    ) : (
                                        <Field.Root>
                                            <Field.Label css={styles.label}>
                                                {translationData.searchLocationLabel}
                                            </Field.Label>
                                            <SearchLocation
                                                autocompleteData={autocompleteData}
                                                inputProps={{
                                                    inputProps: {
                                                        ...register('location'),
                                                        'data-testid':
                                                            'search-engine-location-input',
                                                    },
                                                }}
                                                w={'100%'}
                                            />
                                        </Field.Root>
                                    )}
                                    <Field.Root>
                                        <Field.Label css={styles.label}>
                                            {translationData.rangePickerLabel}
                                        </Field.Label>
                                        <RangePicker
                                            selectedDates={[
                                                getValues('dates.checkInDate'),
                                                getValues('dates.checkOutDate'),
                                            ]}
                                            onRangeChange={(dates: RangePickerDates) => {
                                                setValue('dates.checkInDate', dates[0]);
                                                setValue('dates.checkOutDate', dates[1]);
                                                onInputValueUpdated();
                                            }}
                                            inputProps={{
                                                'data-testid': 'search-engine-range-picker',
                                            }}
                                            isWebComponent={isWebComponentVersion}
                                            isMobileBrowser={true}
                                            flexDates={{
                                                withFlexDates: true,
                                                onFlexDateSelect: (flexDays: number) => {
                                                    setValue('flexDates', flexDays);
                                                    onInputValueUpdated();
                                                },
                                                selectedFlexDays: watch('flexDates'),
                                            }}
                                        />
                                    </Field.Root>
                                    <Field.Root>
                                        <Field.Label css={styles.label}>
                                            {translationData.guestsPickerLabel}
                                        </Field.Label>
                                        <GuestsPicker
                                            inputData={getValues('guests')}
                                            noArrow={true}
                                            onInputDataChanged={(
                                                data: GuestsPickerInputDataModel,
                                            ) => {
                                                setValue('guests', {
                                                    adultsCount: data.adultsCount || 1,
                                                    childrenCount: data.childrenCount || 0,
                                                    childrenAges: data.childrenAges
                                                        ? data.childrenAges.filter(
                                                              (age): age is number =>
                                                                  age !== undefined,
                                                          )
                                                        : [],
                                                });
                                                onInputValueUpdated();
                                            }}
                                            triggerProps={{
                                                pl: 3,
                                                width: 'full',
                                                'data-testid': 'search-engine-guests-picker',
                                            }}
                                            isMobileBrowser={true}
                                        />
                                    </Field.Root>
                                    {!isMapSearch ? (
                                        <SearchEngineInstantReservationCheckbox
                                            onInputValueUpdated={onInputValueUpdated}
                                            setValue={setValue}
                                            watch={watch}
                                        />
                                    ) : null}
                                    <Button
                                        type="submit"
                                        onClick={handleSubmit}
                                        disabled={isSubmitted}
                                        loading={isLoading}
                                        variant={'solid'}
                                        mt={4}
                                        data-testid="search-engine-submit-button"
                                        css={styles.button}
                                    >
                                        <IconsLinear.Search fontSize={'xl'} />
                                        {translationData.buttonSubmitLabel}
                                    </Button>
                                </Flex>
                            </form>
                        </Drawer.Body>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};
